import { useEffect, useMemo, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import axios from 'axios';
import { Search, MessageCircle, FileDown, UserPlus, Clock, Tag } from 'lucide-react';
import { fmtPromoDate } from '../components/PromoBadge';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const Price = ({ label, value, old, hint, big }) => {
  if (value == null) return null;
  return (
    <div className="flex items-baseline justify-between gap-2">
      <span className="text-zinc-500 text-[10px] uppercase tracking-wider">{label}{hint && <span className="normal-case tracking-normal text-zinc-600"> · {hint}</span>}</span>
      <span className="flex items-baseline gap-1.5">
        {old != null && <span className="text-zinc-500 text-xs line-through">{old}€</span>}
        <span className={`${big ? 'text-lg' : 'text-base'} font-semibold ${old != null ? 'text-red-400' : 'text-[color:var(--color-primary)]'}`}>{value}€</span>
      </span>
    </div>
  );
};

const PublicProductCard = ({ p, whatsapp, th }) => {
  const wa = `https://wa.me/${whatsapp}?text=${encodeURIComponent(`Bonjour, je souhaite commander : ${p.name} (${p.brand})`)}`;
  return (
    <div className="bg-[color:var(--color-surface)] border border-white/10 overflow-hidden flex flex-col rounded-2xl hover:shadow-xl hover:-translate-y-1 transition-all duration-300" data-testid={`public-product-${p.id}`}>
      <div className="relative aspect-square bg-black overflow-hidden shrink-0">
        <img src={p.image_url} alt={p.name} loading="lazy" className="absolute inset-0 w-full h-full object-cover"
          onError={(e) => { e.target.src = 'https://images.unsplash.com/photo-1541643600914-78b084683601?w=400'; }} />
        {p.promo && (
          <span className="absolute top-2 right-2 bg-red-600 text-white text-[10px] px-2 py-0.5 uppercase tracking-wider font-semibold inline-flex items-center gap-1">
            <Tag className="w-3 h-3" /> Promo{p.promo.percent ? ` -${p.promo.percent}%` : ''}
          </span>
        )}
        <span className={`absolute bottom-2 left-2 text-[10px] px-2.5 py-0.5 uppercase tracking-wider font-bold rounded-full shadow-sm text-white ${p.stock <= 5 ? 'bg-amber-500' : 'bg-[color:var(--color-emerald)]'}`} data-testid={`public-stock-${p.id}`}>
          {p.stock} dispo{p.stock > 1 ? 's' : ''}
        </span>
      </div>
      <div className="p-3 flex-1 flex flex-col gap-2">
        <div>
          <p className="inline-block max-w-full text-[10px] font-bold uppercase tracking-wider text-[color:var(--color-emerald)] bg-[color:var(--color-emerald-light)] px-2 py-0.5 rounded-md truncate mb-1">{p.brand}</p>
          <h3 className="text-white text-sm font-semibold leading-tight">{p.name}</h3>
        </div>
        <div className="space-y-1 border-t border-white/5 pt-2">
          <Price label="Détail" value={p.price_detail} old={p.price_detail_original} big />
          <Price label="Semi-gros" value={p.price_semi_gros} old={p.price_semi_gros_original} hint={`dès ${th.semi_gros_total} art. au panier`} />
          <Price label="Gros" value={p.price_gros} old={p.price_gros_original} hint={`dès ${th.gros_per_product} pcs`} />
        </div>
        {p.promo && (
          <p className="text-red-400 text-[11px] inline-flex items-center gap-1"><Clock className="w-3 h-3" /> Jusqu'au {fmtPromoDate(p.promo.ends_at)}</p>
        )}
        <a href={wa} target="_blank" rel="noopener noreferrer"
          className="mt-auto inline-flex items-center justify-center gap-2 bg-[color:rgb(37_211_102/0.15)] border border-[color:rgb(37_211_102/0.40)] text-[#25D366] text-xs py-2 hover:bg-[color:rgb(37_211_102/0.25)] transition-colors"
          data-testid={`public-order-${p.id}`}>
          <MessageCircle className="w-3.5 h-3.5" /> Commander
        </a>
      </div>
    </div>
  );
};

export default function PublicCataloguePage() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [search, setSearch] = useState('');
  const [brand, setBrand] = useState('all');
  const [promoOnly, setPromoOnly] = useState(false);

  useEffect(() => {
    axios.get(`${API_URL}/api/public/catalogue/${token}`)
      .then(r => setData(r.data))
      .catch(e => setError(e?.response?.status === 404 ? "Ce lien n'est plus valide. Demandez le nouveau lien à Ô'Delicieuse." : 'Impossible de charger le catalogue.'));
  }, [token]);

  const brands = useMemo(() => [...new Set((data?.products || []).map(p => p.brand).filter(Boolean))].sort(), [data]);
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return (data?.products || []).filter(p =>
      (brand === 'all' || p.brand === brand) &&
      (!promoOnly || p.promo) &&
      (!q || p.name?.toLowerCase().includes(q) || p.brand?.toLowerCase().includes(q))
    );
  }, [data, search, brand, promoOnly]);

  const promoCount = (data?.products || []).filter(p => p.promo).length;
  const pdfUrl = `${API_URL}/api/public/catalogue/${token}/pdf`;

  return (
    <div className="min-h-screen bg-[color:var(--color-bg)] text-white" data-testid="public-catalogue-page">
      <header className="sticky top-0 z-20 bg-[color:rgb(var(--surface-rgb)/0.92)] backdrop-blur border-b border-[color:rgb(var(--primary-rgb)/0.20)]">
        <div className="max-w-6xl mx-auto px-4 py-4 flex flex-wrap items-center gap-4">
          <div className="mr-auto">
            <p className="text-[color:var(--color-primary)] text-2xl leading-none" style={{ fontFamily: 'Cormorant Garamond, serif' }}>Ô'Delicieuse</p>
            <p className="text-zinc-500 text-[10px] uppercase tracking-[0.3em]">Wholesale · Catalogue pro en direct</p>
          </div>
          <a href={pdfUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 border border-white/15 text-zinc-200 text-xs px-3 py-2 hover:border-[color:var(--color-primary)] hover:text-[color:var(--color-primary)] transition-colors" data-testid="public-pdf-btn">
            <FileDown className="w-4 h-4" /> PDF illustré
          </a>
          <Link to="/inscription" className="inline-flex items-center gap-2 bg-[color:var(--color-primary)] text-black text-xs font-medium px-3 py-2 hover:bg-[color:var(--color-primary-hover)] transition-colors" data-testid="public-register-btn">
            <UserPlus className="w-4 h-4" /> Créer un compte pro
          </Link>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-4 py-6">
        {error && <div className="border border-red-500/30 bg-red-500/10 text-red-300 p-6 text-center" data-testid="public-error">{error}</div>}
        {!data && !error && <p className="text-zinc-500 text-center py-20">Chargement du catalogue…</p>}
        {data && (
          <>
            <div className="flex flex-col md:flex-row gap-3 mb-6">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Rechercher un parfum ou une marque…"
                  className="w-full bg-[color:var(--color-surface)] border border-white/10 pl-10 pr-3 py-3 text-sm outline-none focus:border-[color:var(--color-primary)]" data-testid="public-search" />
              </div>
              <select value={brand} onChange={e => setBrand(e.target.value)} className="bg-[color:var(--color-surface)] border border-white/10 text-zinc-300 px-3 py-3 text-sm outline-none focus:border-[color:var(--color-primary)]" data-testid="public-brand-filter">
                <option value="all">Toutes les marques</option>
                {brands.map(b => <option key={b} value={b}>{b}</option>)}
              </select>
              {promoCount > 0 && (
                <button type="button" onClick={() => setPromoOnly(v => !v)} className={`px-4 py-3 text-sm border transition-colors ${promoOnly ? 'border-red-500 bg-red-500/15 text-red-300' : 'border-white/10 text-zinc-300'}`} data-testid="public-promo-filter">
                  Promos ({promoCount})
                </button>
              )}
            </div>
            <p className="text-zinc-500 text-xs mb-4" data-testid="public-count">
              {filtered.length} produit{filtered.length > 1 ? 's' : ''} en stock · Semi-gros dès {data.thresholds.semi_gros_total} articles au panier · Gros dès {data.thresholds.gros_per_product} pièces d'un même parfum ou {data.thresholds.gros_total} articles
            </p>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
              {filtered.map(p => <PublicProductCard key={p.id} p={p} whatsapp={data.whatsapp} th={data.thresholds} />)}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
